import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useOnline } from "@/lib/connectivity";
import { NeedsInternetNote } from "@/components/Offline";
import { ImageUpload } from "@/components/ImageUpload";
import { CostField } from "@/components/CostField";
import { useCan } from "@/context/AuthContext";

const emptyBatch = () => ({ batch_no: "", expiry: "", qty: "" });

export function QuickAddDialog({ open, onOpenChange, biz, initialBarcode = "", onCreated }) {
  const pharmacy = biz.business_type === "pharmacy";
  const online = useOnline();
  const [f, setF] = useState({ name: "", price: "", barcode: "", tax_rate: "", stock: "" });
  const [batches, setBatches] = useState([emptyBatch()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [cost, setCost] = useState("");
  const can = useCan();

  useEffect(() => {
    if (open) {
      setF({ name: "", price: "", barcode: initialBarcode || "", tax_rate: "", stock: "" });
      setBatches([emptyBatch()]);
      setError("");
      setPhoto(null);
      setCost("");
    }
  }, [open, initialBarcode]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setB = (i, k) => (e) => setBatches((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: e.target.value } : b)));
  const batchesOk = !pharmacy || batches.every((b) => b.batch_no.trim() && b.expiry && b.qty !== "");
  const ready = f.name.trim() && f.price.trim() && batchesOk;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    try {
      const { stock, ...rest } = f;
      const body = { ...rest, tax_rate: f.tax_rate.trim() || null, barcode: f.barcode.trim() || null };
      if (!pharmacy && stock.trim()) {
        if (!/^-?\d+$/.test(stock.trim())) throw new Error("Stock must be a whole number");
        body.stock = parseInt(stock, 10);
      }
      if (pharmacy) body.batches = batches.map((b) => ({ ...b, qty: parseInt(b.qty, 10) || 0 }));
      let { data } = await api.post("/products", body);
      if (cost.trim() && can("profit")) await api.put(`/products/${data.id}/cost`, { cost: cost.trim() });
      if (photo) data = { ...data, photo_url: (await api.put(`/store/products/${data.id}/photo`, { photo_url: photo })).data.photo_url };
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Quick add product</DialogTitle>
          <DialogDescription>Available on the register immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
            <ImageUpload label="Photo (optional)" value={photo} onChange={setPhoto} client={api} testid="quick-add-photo" />
          <div className="space-y-1.5">
            <Label htmlFor="qa-name">Name</Label>
            <Input id="qa-name" autoFocus className="h-11" value={f.name} onChange={set("name")} data-testid="quick-add-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qa-price">Price ({biz.currency}{biz.tax_inclusive ? ", incl. tax" : ", excl. tax"})</Label>
              <Input id="qa-price" inputMode="decimal" className="money h-11" value={f.price} onChange={set("price")} data-testid="quick-add-price-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qa-tax">Tax rate %</Label>
              <Input id="qa-tax" inputMode="decimal" className="money h-11" value={f.tax_rate} onChange={set("tax_rate")} placeholder={`${biz.tax_rate} (default)`} data-testid="quick-add-tax-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="qa-bc">Barcode (optional)</Label>
            <Input id="qa-bc" className="money h-11" value={f.barcode} onChange={set("barcode")} data-testid="quick-add-barcode-input" />
          </div>
          {can("profit") && <CostField biz={biz} value={cost} onChange={setCost} testid="quick-add-cost-input" />}
          {!pharmacy && (
            <div className="space-y-1.5">
              <Label htmlFor="qa-stock">Stock on hand (optional)</Label>
              <Input id="qa-stock" inputMode="numeric" className="money h-11" value={f.stock} onChange={set("stock")} placeholder="Not tracked" data-testid="quick-add-stock-input" />
            </div>
          )}
          {pharmacy && (
            <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="quick-add-batches">
              <div className="flex items-center justify-between">
                <span className="eyebrow !text-amber-800">Batches · FEFO</span>
                <button type="button" onClick={() => setBatches((b) => [...b, emptyBatch()])} className="flex h-9 items-center gap-1 rounded-lg px-2 text-sm font-semibold text-slate-800 hover:bg-amber-100" data-testid="quick-add-add-batch-button">
                  <Plus className="h-4 w-4" /> Batch
                </button>
              </div>
              {batches.map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_64px_36px] items-center gap-2">
                  <Input placeholder="Batch no" className="h-10 font-mono text-sm" value={b.batch_no} onChange={setB(i, "batch_no")} data-testid={`quick-add-batch-no-${i}`} />
                  <Input type="date" className="h-10 text-sm" value={b.expiry} onChange={setB(i, "expiry")} data-testid={`quick-add-batch-expiry-${i}`} />
                  <Input type="number" min={0} placeholder="Qty" className="money h-10 text-sm" value={b.qty} onChange={setB(i, "qty")} data-testid={`quick-add-batch-qty-${i}`} />
                  <button type="button" disabled={batches.length === 1} onClick={() => setBatches((bs) => bs.filter((_, j) => j !== i))} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-rose-600 disabled:opacity-30" data-testid={`quick-add-batch-remove-${i}`} aria-label="Remove batch">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid="quick-add-error">{error}</div>}
          {!online && <NeedsInternetNote />}
          <button type="submit" disabled={!ready || busy || !online} data-testid="quick-add-submit-button" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:bg-slate-300">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save product
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
